import { Icon, InfoTooltip } from '@ps/isp-web-component-library';
import { PriceBoost } from '@ps/isp-web-component-library/icons/PriceBoost';
import type {
  MultipleBetType,
  MultipleOpportunity,
  SingleOpportunity
} from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import {
  getIsAvailableOpportunity,
  getIsSgmInvalidOpportunity,
  getIsSgmOpportunity,
  getIsSingleAvailableOpportunity,
  getIsSingleOpportunity,
  getIsSingleUnavailableOpportunity,
  getIsSuspendedOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { select } from '@swf/core/store';
import AnimatedInPlayIcon from '@swf/sports/components/AnimatedInPlayIcon/AnimatedInPlayIcon';
import { getMultipleName } from '@swf/sports/utils/getMultipleName';
import getRunnerNameWithHandicap from '@swf/sports/utils/getRunnerNameWithHandicap';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';

import MultiplesTooltipContent from '../MultiplesTooltipContent/MultiplesTooltipContent';
import RemoveButton from '../RemoveButton/RemoveButton';

import {
  TXTCLI_Suspended,
  TXTWCL_Close,
  TXTWCL_Multiple_bet_information,
  TXTWCL_Unavailable,
  TXTWCL_odds_boost
} from './Heading.messages.json';
import styles from './Heading.scss';
import OddsDisplay from './components/OddsDisplay/OddsDisplay';

interface Props {
  opportunityRef: React.RefObject<HTMLDivElement>;
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const Heading: React.FC<Props> = ({ opportunity, opportunityRef }) => {
  const { formatMessage } = useRegulatoryIntl();

  const isSingleOpportunity = getIsSingleOpportunity(opportunity);
  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isSingleUnavailableOpportunity = getIsSingleUnavailableOpportunity(opportunity);
  const isSuspendedOpportunity = getIsSuspendedOpportunity(opportunity);
  const isSgmOpportunity = getIsSgmOpportunity(opportunity);
  const isSgmInvalidOpportunity = getIsSgmInvalidOpportunity(opportunity);
  const selectedPromotionPicker = useSelector(select.betSlip.selectedPromotionPicker);

  return (
    <>
      {isSingleOpportunity ? (
        <h4 className={classNames(styles.name, !isAvailableOpportunity && styles.isSuspended)}>
          {getRunnerNameWithHandicap({
            runnerName: opportunity.name,
            runnerResultType: isSingleAvailableOpportunity
              ? opportunity.runnerResultType
              : undefined,
            handicap: isAvailableOpportunity ? opportunity.handicap : 0
          })}
        </h4>
      ) : (
        <div className={styles.multipleNameWrapper}>
          <h4
            className={classNames(styles.name, {
              [styles.isSuspended]: !isAvailableOpportunity && !isSgmInvalidOpportunity
            })}
          >
            {getMultipleName({
              betType: opportunity.type as MultipleBetType,
              numberOfLines: opportunity.numberOfLines,
              isSgm: isSgmOpportunity
            })}
          </h4>
          {!isSgmInvalidOpportunity && opportunity.eventInPlay ? (
            <AnimatedInPlayIcon className={styles.inPlayIcon} />
          ) : null}

          <InfoTooltip
            parentRef={opportunityRef}
            closeText={formatMessage(TXTWCL_Close)}
            labelText={formatMessage(TXTWCL_Multiple_bet_information)}
            isDisabled={Boolean(selectedPromotionPicker)}
          >
            <MultiplesTooltipContent
              betType={opportunity.type as MultipleBetType}
              isSgm={isSgmOpportunity}
            />
          </InfoTooltip>
        </div>
      )}

      {isSingleAvailableOpportunity && isOddsBoostMarket(opportunity.marketType) && (
        <Icon
          icon={PriceBoost}
          label={formatMessage(TXTWCL_odds_boost)}
          size="x1"
          className={styles.oddsBoostIcon}
        />
      )}

      <div className={styles.right}>
        {isAvailableOpportunity && opportunity.numberOfLines === 1 && (
          <OddsDisplay opportunity={opportunity} />
        )}

        {isSuspendedOpportunity && (
          <span className={styles.status}>
            <Translation {...TXTCLI_Suspended} />
          </span>
        )}

        {isSingleUnavailableOpportunity && (
          <span className={styles.status}>
            <Translation {...TXTWCL_Unavailable} />
          </span>
        )}

        {isSingleOpportunity && <RemoveButton runnerId={opportunity.runnerId} />}
      </div>
    </>
  );
};

export default Heading;
